import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  IconButton,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Groups,
  WatchLater,
  CheckCircle,
  LocalFireDepartment,
  EventSeat,
  TrendingUp,
  Refresh,
  BarChart
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Analytics - Mobile-First Analytics View
 */
function LibrarianAnalytics({ showSnackbar }) {
  const { user } = useContext(AuthContext);

  const [library, setLibrary] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [pastSessions, setPastSessions] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);

      // Fetch all data
      const [studentsRes, activeSessionsRes, pastSessionsRes, bookingsRes] = await Promise.all([
        api.get('/librarian/students'),
        api.get('/librarian/sessions', { params: { library_id: myLibrary.id, active: true } }),
        api.get('/librarian/sessions', { params: { library_id: myLibrary.id, active: false } }),
        api.get('/bookings', { params: { library_id: myLibrary.id } })
      ]);

      setStudents(studentsRes.data.data.students || []);
      setActiveSessions(activeSessionsRes.data.data.sessions || []);
      setPastSessions(pastSessionsRes.data.data.sessions || []);

      const bookings = bookingsRes.data.data.bookings || [];
      setUpcomingBookings(bookings.filter(b => b.status === 'active' || b.status === 'upcoming'));
      setPastBookings(bookings.filter(b => b.status === 'completed' || b.status === 'cancelled'));
    } catch (err) {
      console.error('Error fetching analytics:', err);
      showSnackbar('Failed to load analytics', 'error');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading analytics...</Typography>
      </Box>
    );
  }

  // Calculate metrics
  const totalSessions = activeSessions.length + pastSessions.length;
  const totalBookings = upcomingBookings.length + pastBookings.length;
  const completedBookings = pastBookings.filter(b => b.status === 'completed').length;
  const completionRate = totalBookings > 0 ? Math.round((completedBookings / totalBookings) * 100) : 0;
  const occupancyRate = library?.capacity ? Math.round((activeSessions.length / library.capacity) * 100) : 0;

  // Calculate avg session duration
  const avgSessionDuration = pastSessions.length > 0
    ? Math.round(
        pastSessions.reduce((sum, session) => {
          if (session.entered_at && session.exited_at) {
            const duration = (new Date(session.exited_at) - new Date(session.entered_at)) / 1000 / 60;
            return sum + duration;
          }
          return sum;
        }, 0) / pastSessions.filter(s => s.exited_at).length
      )
    : 0;

  // Peak hours analysis
  const allSessions = [...activeSessions, ...pastSessions];
  const hourCounts = new Array(24).fill(0);
  allSessions.forEach(session => {
    const hour = new Date(session.entered_at).getHours();
    hourCounts[hour]++;
  });
  const maxHourCount = Math.max(...hourCounts, 1);
  const peakHour = hourCounts.indexOf(maxHourCount);

  // Engagement rate
  const engagementRate = students.length > 0 ? Math.round((activeSessions.length / students.length) * 100) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Analytics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Library performance metrics
                </Typography>
              </Box>
              <IconButton
                onClick={fetchAnalyticsData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Key Metrics */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.brand,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Total Sessions
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {totalSessions}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {activeSessions.length} active
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <Groups />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Avg Duration
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {avgSessionDuration}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        minutes
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <WatchLater />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.coin,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Completion
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {completionRate}%
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        of bookings
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <CheckCircle />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.coin,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Peak Hour
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {peakHour}:00
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        most check-ins
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <LocalFireDepartment />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>

          {/* Capacity Utilization */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    background: GRADIENTS.coin,
                    width: 48,
                    height: 48
                  }}
                >
                  <EventSeat />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="700">
                    Capacity Utilization
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current library occupancy
                  </Typography>
                </Box>
              </Box>

              <Box textAlign="center" py={2}>
                <Typography
                  variant="h2"
                  fontWeight="800"
                  sx={{
                    background: GRADIENTS.coin,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {occupancyRate}%
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {activeSessions.length} of {library?.capacity || 0} seats occupied
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={occupancyRate}
                sx={{
                  height: 12,
                  borderRadius: RADIUS.md,
                  bgcolor: 'action.hover',
                  '& .MuiLinearProgress-bar': {
                    background: GRADIENTS.coin,
                    borderRadius: RADIUS.md
                  }
                }}
              />
            </CardContent>
          </MotionCard>

          {/* Student Engagement */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                    width: 48,
                    height: 48
                  }}
                >
                  <TrendingUp />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="700">
                    Student Engagement
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active vs total students
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.md }}>
                    <Typography variant="h3" fontWeight="800" color="primary">
                      {students.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Students
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box textAlign="center" p={2} sx={{ bgcolor: 'action.hover', borderRadius: RADIUS.md }}>
                    <Typography variant="h3" fontWeight="800" color="success.main">
                      {activeSessions.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Currently Active
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box mb={1}>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Engagement Rate
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {engagementRate}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={engagementRate}
                  sx={{
                    height: 8,
                    borderRadius: RADIUS.sm,
                    bgcolor: 'action.hover',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient(90deg, #4A90A4 0%, #2D6B7D 100%)',
                      borderRadius: RADIUS.sm
                    }
                  }}
                />
              </Box>
            </CardContent>
          </MotionCard>

          {/* Peak Hours Chart */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Avatar
                  sx={{
                    background: GRADIENTS.brand,
                    width: 48,
                    height: 48
                  }}
                >
                  <BarChart />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="700">
                    Peak Hours
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Check-in distribution
                  </Typography>
                </Box>
              </Box>

              <Box>
                {[9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map((hour) => (
                  <Box key={hour} mb={1.5}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" color="text.secondary">
                        {hour}:00
                      </Typography>
                      <Chip
                        label={`${hourCounts[hour]} check-ins`}
                        size="small"
                        color={hour === peakHour ? 'primary' : 'default'}
                        sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(hourCounts[hour] / maxHourCount) * 100}
                      sx={{
                        height: 6,
                        borderRadius: RADIUS.sm,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          background: hour === peakHour
                            ? GRADIENTS.brand
                            : 'linear-gradient(90deg, #4A90A4 0%, #2D6B7D 100%)',
                          borderRadius: RADIUS.sm
                        }
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibrarianAnalytics;
